import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { MessageSquare, Bug, Lightbulb, MessageCircle, CheckCircle, Clock, AlertCircle } from 'lucide-react';

interface Feedback {
  id: number;
  user_id: number | null;
  email: string;
  name: string | null;
  category: 'bug' | 'feature_request' | 'general';
  subject: string;
  message: string;
  status: 'new' | 'in_progress' | 'resolved';
  admin_notes: string | null;
  resolved_by: number | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
}

interface PaginatedResponse {
  items: Feedback[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export default function FeedbackManagement() {
  const [feedback, setFeedback] = useState<Feedback[]>([]);
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalFeedback, setTotalFeedback] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [adminNotes, setAdminNotes] = useState('');
  const [updating, setUpdating] = useState(false);

  const fetchFeedback = React.useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string | number> = {
        page,
        page_size: 50
      };
      if (statusFilter) params.status_filter = statusFilter;
      if (categoryFilter) params.category_filter = categoryFilter;

      const response = await api.get<PaginatedResponse>('/admin/feedback', { params });
      setFeedback(response.data.items);
      setTotalPages(response.data.total_pages);
      setTotalFeedback(response.data.total);
    } catch (err: unknown) {
      setError('Failed to load feedback');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, categoryFilter]);

  useEffect(() => {
    fetchFeedback();
  }, [fetchFeedback]);

  const handleSelectFeedback = (item: Feedback) => {
    setSelectedFeedback(item);
    setAdminNotes(item.admin_notes || '');
  };

  const handleUpdateStatus = async (newStatus: 'new' | 'in_progress' | 'resolved') => {
    if (!selectedFeedback) return;

    try {
      setUpdating(true);
      await api.put(`/admin/feedback/${selectedFeedback.id}`, {
        status: newStatus,
        admin_notes: adminNotes || null
      });

      // Refresh the list
      await fetchFeedback();

      // Update selected feedback
      const updatedResponse = await api.get<Feedback>(`/admin/feedback/${selectedFeedback.id}`);
      setSelectedFeedback(updatedResponse.data);

      setError('');
    } catch (err: unknown) {
      setError('Failed to update feedback');
      console.error(err);
    } finally {
      setUpdating(false);
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'bug':
        return <Bug className="h-5 w-5 text-red-600" />;
      case 'feature_request':
        return <Lightbulb className="h-5 w-5 text-yellow-600" />;
      default:
        return <MessageCircle className="h-5 w-5 text-secondary" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      new: { icon: <AlertCircle className="h-4 w-4" />, bg: 'bg-moonbone', text: 'text-ink', label: 'New' },
      in_progress: { icon: <Clock className="h-4 w-4" />, bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'In Progress' },
      resolved: { icon: <CheckCircle className="h-4 w-4" />, bg: 'bg-teal-100', text: 'text-primary-dark', label: 'Resolved' }
    };
    const badge = badges[status as keyof typeof badges] || badges.new;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.icon}
        {badge.label}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Feedback Management</h1>
          <p className="text-gray-600">Review and manage user feedback</p>
        </div>
        <div className="text-sm text-gray-600">
          Total: <span className="font-semibold">{totalFeedback}</span> feedback items
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow flex flex-wrap gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
          >
            <option value="">All</option>
            <option value="new">New</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
          <select
            value={categoryFilter}
            onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}
            className="rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
          >
            <option value="">All</option>
            <option value="bug">Bug</option>
            <option value="feature_request">Feature Request</option>
            <option value="general">General</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Feedback List */}
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-8">Loading feedback...</div>
          ) : feedback.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No feedback found</div>
          ) : (
            <>
              {feedback.map((item) => (
                <div
                  key={item.id}
                  onClick={() => handleSelectFeedback(item)}
                  className={`bg-white p-4 rounded-lg shadow cursor-pointer hover:shadow-md transition-shadow ${
                    selectedFeedback?.id === item.id ? 'ring-2 ring-primary' : ''
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {getCategoryIcon(item.category)}
                      <span className="text-sm font-medium text-gray-700 capitalize">
                        {item.category.replace('_', ' ')}
                      </span>
                    </div>
                    {getStatusBadge(item.status)}
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">{item.subject}</h3>
                  <p className="text-sm text-gray-600 mb-2 line-clamp-2">{item.message}</p>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>{item.name || item.email}</span>
                    <span>{new Date(item.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center gap-2 mt-4">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 bg-white rounded-md shadow disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1 bg-white rounded-md shadow disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Feedback Detail */}
        <div className="bg-white p-6 rounded-lg shadow">
          {selectedFeedback ? (
            <div className="space-y-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-6 w-6 text-secondary" />
                  <h2 className="text-xl font-semibold">Feedback Details</h2>
                </div>
                {getStatusBadge(selectedFeedback.status)}
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Subject</label>
                <p className="text-gray-900">{selectedFeedback.subject}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Category</label>
                <div className="flex items-center gap-2 mt-1">
                  {getCategoryIcon(selectedFeedback.category)}
                  <span className="capitalize">{selectedFeedback.category.replace('_', ' ')}</span>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Message</label>
                <p className="text-gray-900 whitespace-pre-wrap">{selectedFeedback.message}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">From</label>
                  <p className="text-gray-900">{selectedFeedback.name || 'Anonymous'}</p>
                  <p className="text-sm text-gray-600">{selectedFeedback.email}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Submitted</label>
                  <p className="text-gray-900">
                    {new Date(selectedFeedback.created_at).toLocaleString()}
                  </p>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Admin Notes</label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  rows={4}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                  placeholder="Add internal notes..."
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Update Status</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleUpdateStatus('new')}
                    disabled={updating || selectedFeedback.status === 'new'}
                    className="flex-1 px-3 py-2 bg-primary text-white rounded-md hover:bg-primary-dark disabled:opacity-50"
                  >
                    Mark as New
                  </button>
                  <button
                    onClick={() => handleUpdateStatus('in_progress')}
                    disabled={updating || selectedFeedback.status === 'in_progress'}
                    className="flex-1 px-3 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:opacity-50"
                  >
                    In Progress
                  </button>
                  <button
                    onClick={() => handleUpdateStatus('resolved')}
                    disabled={updating || selectedFeedback.status === 'resolved'}
                    className="flex-1 px-3 py-2 bg-primary text-white rounded-md hover:bg-primary-dark disabled:opacity-50"
                  >
                    Resolve
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <MessageSquare className="h-12 w-12 mx-auto mb-2 text-gray-400" />
              <p>Select a feedback item to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
